import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Trash2, DatabaseZap, RefreshCw } from "lucide-react";
import { clearPackageCacheAll, rerenderLessonPackage } from "../../lib/api";

/**
 * Admin maintenance control — clear the Complete Lesson Package cache.
 *
 * Why this exists: package_cache is content-hashed by activity content
 * only (no renderer/code version in the hash). After a deploy that
 * changes package rendering (e.g. the Jul 2026 phase-tag fix), cached
 * packages keep serving pre-deploy bytes until an admin clears them.
 * This is the visible button for the existing DELETE /api/admin/package-cache
 * hatch. Touches ONLY package_cache — never diagrams / slide specs.
 */
const PackageCachePanel = () => {
  const [clearing, setClearing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const [lessonId, setLessonId] = useState("");
  const [lessonIdx, setLessonIdx] = useState("");
  const [rerendering, setRerendering] = useState(false);
  const [rerenderResult, setRerenderResult] = useState(null);
  const [rerenderError, setRerenderError] = useState(null);

  const handleRerender = async () => {
    const id = lessonId.trim();
    if (!id) return;
    setRerendering(true);
    setRerenderResult(null);
    setRerenderError(null);
    try {
      const idx = lessonIdx.trim() === "" ? null : Number(lessonIdx.trim());
      const res = await rerenderLessonPackage(id, idx);
      setRerenderResult(res);
      toast.success("Lesson package re-rendered.", {
        description: `${(res?.bytes || 0).toLocaleString()} bytes · fresh copy is now cached.`,
      });
    } catch (e) {
      const detail =
        e?.response?.data?.detail || e?.message || "Unknown error";
      setRerenderError(detail);
      toast.error("Could not re-render this lesson", { description: detail });
    } finally {
      setRerendering(false);
    }
  };

  const handleClearAll = async () => {
    setClearing(true);
    try {
      const res = await clearPackageCacheAll();
      const deleted = res?.deleted ?? 0;
      setLastResult(deleted);
      toast.success(
        deleted === 0
          ? "Package cache was already empty — nothing to clear."
          : `Cleared ${deleted} cached lesson package${deleted === 1 ? "" : "s"}.`,
        { description: "Teachers will now get freshly-rendered packages." }
      );
    } catch (e) {
      toast.error("Could not clear the package cache", {
        description: e?.response?.data?.detail || e?.message || "Unknown error",
      });
    } finally {
      setClearing(false);
      setConfirming(false);
    }
  };

  return (
    <div className="ae-card p-6 md:p-7" data-testid="admin-package-cache-card">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 rounded-lg bg-[#96ded1]/25 p-2 text-[#1c2d3d]">
          <DatabaseZap className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-bold text-[#1c2d3d]">
            Lesson-package cache
          </h3>
          <p className="mt-1 text-sm text-[#4a6984] leading-relaxed max-w-2xl">
            Complete Lesson Package PDFs are cached by lesson content. When a
            deploy changes how packages render (for example the phase-tag
            fix), cached copies keep serving the old version until you clear
            them here. Run this once <strong>right after a deploy</strong> that
            touches package rendering. Individual PDF / DOCX / PPTX / script
            downloads are never cached and update on their own.
          </p>

          {lastResult != null && (
            <p
              className="mt-3 text-sm font-semibold text-[#256e64]"
              data-testid="admin-package-cache-last-result"
            >
              Last run cleared {lastResult} package{lastResult === 1 ? "" : "s"}.
            </p>
          )}

          <div className="mt-5">
            {!confirming ? (
              <button
                type="button"
                onClick={() => setConfirming(true)}
                data-testid="admin-package-cache-clear-btn"
                className="inline-flex items-center gap-2 rounded-full bg-[#1c2d3d] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#4a6984]"
              >
                <Trash2 className="h-4 w-4" />
                Clear all cached packages
              </button>
            ) : (
              <div
                className="flex flex-wrap items-center gap-3"
                data-testid="admin-package-cache-confirm"
              >
                <span className="text-sm font-medium text-[#1c2d3d]">
                  Clear every cached package now?
                </span>
                <button
                  type="button"
                  disabled={clearing}
                  onClick={handleClearAll}
                  data-testid="admin-package-cache-confirm-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-[#c0392b] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#a93226] disabled:opacity-60"
                >
                  {clearing ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                  Yes, clear all
                </button>
                <button
                  type="button"
                  disabled={clearing}
                  onClick={() => setConfirming(false)}
                  data-testid="admin-package-cache-cancel-btn"
                  className="rounded-full px-4 py-2 text-sm font-semibold text-[#4a6984] transition-colors hover:bg-[#f4f5f7] disabled:opacity-60"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>

          {/* Re-render one lesson by ID — forces a fresh render through the
              same path a normal download uses. Mobile-first (390px). */}
          <div
            className="mt-7 border-t border-[#4a6984]/15 pt-6"
            data-testid="admin-rerender-section"
          >
            <div className="flex items-start gap-3">
              <div className="mt-0.5 rounded-lg bg-[#96ded1]/25 p-2 text-[#1c2d3d]">
                <RefreshCw className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <h4 className="text-base font-bold text-[#1c2d3d]">
                  Re-render one lesson
                </h4>
                <p className="mt-1 text-sm text-[#4a6984] leading-relaxed max-w-2xl">
                  Force a fresh Complete Lesson Package for a single lesson,
                  rebuilt from its stored content through the exact path a
                  teacher download uses. No AI is called and the lesson is not
                  changed. For a unit, add the lesson number.
                </p>

                <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end">
                  <label className="flex-1 min-w-0">
                    <span className="block text-xs font-semibold uppercase tracking-wide text-[#4a6984]">
                      Lesson ID
                    </span>
                    <input
                      type="text"
                      value={lessonId}
                      onChange={(e) => setLessonId(e.target.value)}
                      placeholder="e.g. b500f617-dfe6-4fdc-b8d6-aa0ab4d46a03"
                      data-testid="admin-rerender-id-input"
                      className="mt-1 w-full rounded-lg border border-[#4a6984]/25 px-3 py-2.5 text-sm text-[#1c2d3d] focus:border-[#256e64] focus:outline-none focus:ring-1 focus:ring-[#256e64]"
                    />
                  </label>
                  <label className="w-full sm:w-28">
                    <span className="block text-xs font-semibold uppercase tracking-wide text-[#4a6984]">
                      Lesson&nbsp;# <span className="font-normal lowercase">(units)</span>
                    </span>
                    <input
                      type="number"
                      inputMode="numeric"
                      value={lessonIdx}
                      onChange={(e) => setLessonIdx(e.target.value)}
                      placeholder="—"
                      data-testid="admin-rerender-index-input"
                      className="mt-1 w-full rounded-lg border border-[#4a6984]/25 px-3 py-2.5 text-sm text-[#1c2d3d] focus:border-[#256e64] focus:outline-none focus:ring-1 focus:ring-[#256e64]"
                    />
                  </label>
                  <button
                    type="button"
                    onClick={handleRerender}
                    disabled={rerendering || !lessonId.trim()}
                    data-testid="admin-rerender-btn"
                    className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-full bg-[#256e64] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#1c554d] disabled:opacity-50"
                  >
                    {rerendering ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Re-rendering…
                      </>
                    ) : (
                      <>
                        <RefreshCw className="h-4 w-4" />
                        Re-render
                      </>
                    )}
                  </button>
                </div>

                {rerenderResult && (
                  <p
                    className="mt-3 text-sm font-semibold text-[#256e64]"
                    data-testid="admin-rerender-success"
                  >
                    Done — fresh package cached ({(rerenderResult.bytes || 0).toLocaleString()} bytes).
                    {rerenderResult.lesson_index != null
                      ? ` Lesson #${rerenderResult.lesson_index}.`
                      : ""}
                  </p>
                )}
                {rerenderError && (
                  <p
                    className="mt-3 text-sm font-semibold text-[#c0392b]"
                    data-testid="admin-rerender-error"
                  >
                    {rerenderError}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PackageCachePanel;
